import React, { FC, useRef, useState } from 'react';
import Label from "../Label/Label";
import ErrorText from "../ErrorText/ErrorText";
import { clss } from '../../../../utils/helpers/styleHelpers';
import Icon from '../../../Icon/Icon';
import { DirectionE, IconColorE } from '../../../../models/enums';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../store/hooks';
import { selectDirection } from '../../../../store/selectors/appSelectors';

import styles from "../../form.module.scss";

type DateFieldPropTypes = {
    name: string;
    label: string;
    errorMessage: string;
    hintText?: string;
    register: any;
    orderIndex: number;
    defaultValue: any;
    style: any;
    props: any;
    onChange: (value: any) => void;
    extraContent: any;
}

const TAB_KEY = 'Tab';

const DateField: FC<DateFieldPropTypes> = (props) => {
    const {
        name,
        label,
        errorMessage,
        hintText,
        register,
        orderIndex,
        defaultValue,
        style,
        props: outerProps,
        onChange,
        extraContent,
    } = props;
    const [date, setDate] = useState(defaultValue ? dayjs(defaultValue).format('DD/MM/YYYY') : 'dd/mm/yyyy');
    const direction = useAppSelector(selectDirection);
    const dateRef = useRef<HTMLInputElement | null>(null);

    // react-hook-form's own onChange/ref have to be kept and composed, not replaced. Spreading
    // register() and then declaring onChange={handleChange} / ref={dateRef} further down overrode
    // both, so RHF never learned the picked date and the field was submitted as `undefined`
    // (the campaign wizard then silently saved the original broadcasting date).
    const {
        onChange: registerOnChange,
        ref: registerRef,
        ...registration
    } = register(name as `${string}`);

    const handleChange = (e: any) => {
        registerOnChange(e);
        const value = e.target.value;
        const formattedValue = value ? dayjs(value).format('DD/MM/YYYY') : 'dd/mm/yyyy';
        setDate(formattedValue);
        onChange(value);
    };

    // The local ref is only used for showPicker(); RHF still needs the element too.
    const setRefs = (element: HTMLInputElement) => {
        registerRef(element);
        dateRef.current = element;
    };

    const preventInput = (e) => {
        if (e.key !== TAB_KEY) {
            e.preventDefault();
        }
    };

    const handleClick = () => {
        showPicker();
    }

    const showPicker = () => {
        try {
            (dateRef.current as HTMLInputElement)?.showPicker?.();
        } catch {
            // showPicker() requires a user gesture; ignore when not allowed (e.g. onFocus/tab)
        }
    };

    return (
        <div key={name} style={{ ...style }}>
            <Label label={label} name={name} hintText={hintText}>
                <div className={styles.dateInputWrapper}>
                    <input
                        type='date'
                        {...registration}
                        className={clss([styles.field, errorMessage ? styles.errorField : ''])}
                        defaultValue={defaultValue}
                        {...outerProps}
                        onChange={handleChange}
                        onKeyDown={preventInput}
                        tabIndex={orderIndex}
                        onClick={handleClick}
                        ref={setRefs}
                    />
                    <span
                        className={styles.datePlaceholder}
                        style={direction === DirectionE.LTR ? {
                            insetInlineStart: 20
                        } : {
                            insetInlineEnd: 20
                        }}
                    >
                        {date}
                    </span>
                    <div className={styles.calendarIconWrapper}>
                        <Icon name='calendar' color={IconColorE.GRAY} className={styles.calendarIcon} />
                    </div>
                </div>
            </Label>
            <ErrorText text={errorMessage} />
            {!!extraContent && extraContent()}
        </div>
    );
};

export default DateField;